import { h, signal, tag } from "omi";

import { ErrorService } from "@/services/errorService";
import { PureComponent } from "@/utils/pureComponent";
import { NWError, NWLoadingSpinner } from "@/components";
import { tailwind } from "@/tailwind";

/**
 * Props for the NWSuspense component.
 * @template T - The type of the component to be loaded.
 */
type SuspenseProps<T> = {
  /**
   * A function that returns a promise resolving to the component to be loaded.
   */
  loadFunc: () => Promise<T>;

  /**
   * A function that renders the loaded component with its props.
   * @param LoadedComponent - The loaded component.
   * @returns JSX element representing the rendered component.
   */
  onLoadComplete: (LoadedComponent: T) => JSX.Element;

  /**
   * An optional loading component to show while the main component is being loaded.
   * If provided, this will be used instead of the default loading spinner.
   * @returns JSX element representing the loading state.
   */
  loadingComponent?: () => JSX.Element;

  /**
   * An optional error component to show if loading fails.
   * Defaults to a simple error message if not provided.
   * @param error - The error that occurred during loading.
   * @returns JSX element representing the error state.
   */
  errorComponent?: (error: Error) => JSX.Element;
};

/**
 * A component that implements suspense-like functionality for asynchronous component loading.
 * @template T - The type of the component to be loaded.
 */
@tag("nw-suspense")
export class NWSuspense<T> extends PureComponent<SuspenseProps<T>> {
  static css = [tailwind];

  private loadedComponent = signal<T | null>(null);
  private error = signal<Error | null>(null);
  private isLoading = signal(true);

  install(): void {
    this.loadComponent().catch(err => {
      ErrorService.report(err);
      console.error("Failed to load component:", err);
    });
  }

  /**
   * Asynchronously loads the component using the provided loadFunc.
   */
  private async loadComponent(): Promise<void> {
    try {
      const component = await this.props.loadFunc();
      this.loadedComponent.value = component;
    } catch (err) {
      this.error.value = err instanceof Error ? err : new Error(String(err));
    } finally {
      this.isLoading.value = false;
    }
  }

  render(): JSX.Element | null {
    if (this.error.value) {
      return this.props.errorComponent ? this.props.errorComponent(this.error.value) : <NWError />;
    }

    if (this.loadedComponent.value) {
      return this.props.onLoadComplete(this.loadedComponent.value);
    }

    if (this.isLoading.value) {
      return this.props.loadingComponent ? this.props.loadingComponent() : <NWLoadingSpinner />;
    }

    return null;
  }
}
